// FIRST, before every other import (security code review R-2): module
// evaluation follows import order, and the router snapshots window.location
// when ./router evaluates. Stripe's return is scrubbed and claimed before that
// (features/payments/returnBootEntry.ts); while PAYMENTS_ENABLED is false this
// resolves to an empty module (vite.config.ts).
import "virtual:lura-payments-boot";
import { initializeAppearance } from "../../../shared/ui/WebHeader";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { ApolloProvider } from "@apollo/client/react";
import { IdentityProvider } from "./auth/IdentityProvider";
import { routeAfterSessionEnd } from "./auth/sessionEndRoute";
import { ConsentProvider } from "./analytics/ConsentContext";
import { ActionNoticeProvider } from "../../../shared/ui/RecaptchaNotice";
import { getApolloClient } from "./lib/apolloClient";
import { LOCALE_PENDING_CLASS, TranslationProvider } from "./i18n/useTranslation";
import { detectLocale, loadedSpanish, loadSpanish } from "./i18n";
import router from "./router";
import { ROUTER_USES_TRANSITIONS } from "./router/transitions";
// Imported for its SIDE EFFECT, and this line is load-bearing.
//
// config/env.ts asserts at module scope that the build's API host and web host
// match its VITE_ENV. Nothing else imports it, so without this the module is
// tree-shaken out and the guard ships in no bundle at all — which is exactly
// what happened when this surface was first scaffolded. `config/env.test.ts`
// pins the assertions; `validate-quality-contracts.js` pins this import.
import "./config/env";
import "./index.css";
// AFTER index.css, not `@import`ed from inside it. CSS requires `@import` to
// precede all other rules, so importing there would put the desktop layer
// BEFORE index.css's own rules and let them win — the reverse of the order it
// was built and verified against, where it loads last. Matches gallery/main.tsx.
import "./desktop.css";
// Last, and never @imported: every rule in it is a single class that must win
// its tie with index.css by coming later, while losing to desktop.css's
// two-class rules at wide widths. See v2.css.
import "./v2.css";
import "./v3.css";
import "../../../shared/ui/v3-chrome.css";
import "../../../shared/ui/loading.css";
import { applyUserTextScale } from "./lib/textScale";
import { installErrorReporting } from "../../../shared/errorReporting";

// A SPANISH READER'S CATALOGUE, STARTED NOW (rc.12 (b), the Web Director):
// at entry evaluation, before any other boot work, so its one round trip runs
// alongside the identity and profile reads. Until it is here #root is mounted
// but not displayed and the text-free boot skeleton shows (the provider lifts
// the class): no English painted or announced on a Spanish visit.
if (detectLocale() === "es" && !loadedSpanish()) {
  document.documentElement.classList.add(LOCALE_PENDING_CLASS);
  void loadSpanish();
}

// Before the first render, so the first paint is already at the reader's size.
applyUserTextScale();

initializeAppearance();

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root element #root is missing from index.html");
}

// Apollo wraps the whole surface rather than a lazy data layout, which is how
// apps/app does it. There, most routes need no data and the split keeps Apollo
// off their critical path. Here EVERY route that matters is a data route — the
// profile, the flow, the appointment screen — so deferring it would only add a
// round trip to the first paint of a booking link.
ReactDOM.createRoot(container).render(
  <React.StrictMode>
    {/* The optional-analytics answer, for the banner and Cookie settings that
        are designed but not yet built. It renders nothing and, with the enable
        flag absent, changes nothing. */}
    <ConsentProvider>
      <TranslationProvider>
        {/* Identity OUTSIDE Apollo, because Apollo's auth link asks for a
            token and the provider is what mints one. Inside, the first query
            could race the identity that authorises it. */}
        {/* A session Backend ended is signed out by IdentityProvider and
            routed here, to the professional's sign-in step. */}
        <IdentityProvider onSessionEnded={() => routeAfterSessionEnd(router)}>
          <ApolloProvider client={getApolloClient()}>
            {/* One reCAPTCHA notice per screen: an action's copy omits the
                footer's (shared/ui/RecaptchaNotice.tsx). */}
            <ActionNoticeProvider>
              {/* No transitions: IF-3, see router/transitions.ts. */}
              <RouterProvider router={router} useTransitions={ROUTER_USES_TRANSITIONS} />
            </ActionNoticeProvider>
          </ApolloProvider>
        </IdentityProvider>
      </TranslationProvider>
    </ConsentProvider>
  </React.StrictMode>
);

// Error reporting starts after the first render, in idle time, and only on
// this environment's own deployed host (see shared/errorReporting). The SDK is
// a separate chunk fetched then, as on App: it used to ride in Book's single
// entry, and on Staging and Prod that made the entry ~25 KiB gzip larger than
// on every build the tests measured (rc.2's Staging run, 2026-10-12). Book is
// still one bundle per route for everything else (bundle-contract.spec.js
// allows exactly this one chunk, only where reporting is on).
if (__LURA_ERROR_REPORTING_DSN__) {
  installErrorReporting({
    surface: "book",
    env: __LURA_ERROR_REPORTING_ENV__,
    dsn: __LURA_ERROR_REPORTING_DSN__,
    commit: __LURA_BUILD_COMMIT__,
    load: () => import("../../../shared/errorReporting/sentryClient"),
  });
}
